import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import {
  type MarketplaceProduct,
  type SortKey,
  SORT_OPTIONS,
  buildCategoryOptions,
  countByPrice,
  formatPrice,
  isFreeProduct,
  resolveSortKey,
  sortProducts
} from '../utils/marketplaceFilters';
import logo from '../assets/pokeshani-logo.png';
import './MarketplacePage.css';
import SEO from './SEO';

const SITE = 'https://pokeshani.com';

const PAGE_SIZE = 1000;
const MAX_PRODUCTS = 40000;

/**
 * How many product cards the grid renders at once.
 *
 * Every card is an <img> served from Supabase Storage, and Storage egress is
 * metered per byte. Rendering the whole catalogue meant one visitor could pull
 * hundreds of megabytes (and crawlers pulled the same again), which is what
 * pushed the project over the Free plan's 5 GB Cached Egress quota. Rendering
 * 24 at a time plus "Load more" keeps a page view in the low-megabyte range.
 */
const UI_PAGE_SIZE = 24;

const MarketplacePage: React.FC = () => {
  const location = useLocation();
  const [products, setProducts] = useState<MarketplaceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>(['all']);
  const [visibleCount, setVisibleCount] = useState(UI_PAGE_SIZE);

  /* Filter state lives in the URL rather than in component state.
     Two reasons:
       1. a filtered view can be linked, bookmarked and shared
       2. the server-rendered marketplace page already emits ?category= links,
          so reading the category from the URL means those links work as soon
          as the app hydrates instead of flashing the unfiltered list */
  const [searchParams, setSearchParams] = useSearchParams();

  const sort: SortKey = resolveSortKey(searchParams.get('sort'));
  const selectedCategory = searchParams.get('category') || 'all';
  const searchQuery = searchParams.get('q') || '';

  /**
   * Merge filter values into the query string. A null/empty/"all"/default
   * value removes the key, so the default view has exactly one URL and no
   * "?sort=" noise, and sort/search variants are not treated as separate
   * pages by search engines.
   */
  const updateParams = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          Object.entries(changes).forEach(([key, value]) => {
            const isDefault =
              !value ||
              value === 'all' ||
              (key === 'sort' && value === resolveSortKey(null));
            if (isDefault) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });
          return next;
        },
        // replace, not push, so typing in the search box does not add a
        // history entry per keystroke
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    fetchProducts();
  }, []);

  // A new filter/sort/search restarts the grid, otherwise "Load more" would
  // keep a stale page size and the first results would be buried.
  useEffect(() => {
    setVisibleCount(UI_PAGE_SIZE);
  }, [sort, selectedCategory, searchQuery]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const fetchProducts = async () => {
    try {
      const allProducts: MarketplaceProduct[] = [];
      for (let page = 0; page * PAGE_SIZE < MAX_PRODUCTS; page += 1) {
        const from = page * PAGE_SIZE;
        const { data, error } = await supabase
          .from('digitalproducts')
          .select('id, title, thumbnail, slug, price, created_at, category, view_count')
          .eq('is_deleted', false)
          .order('created_at', { ascending: false })
          .range(from, from + PAGE_SIZE - 1);
        if (error) {
          console.error('Error fetching products:', error);
          setError('Failed to load products');
          return;
        }
        if (!data || !data.length) break;
        allProducts.push(...(data as MarketplaceProduct[]));
        if (data.length < PAGE_SIZE) break;
      }
      setProducts(allProducts);
      setCategories(buildCategoryOptions(allProducts));
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const { free: freeCount, paid: paidCount } = countByPrice(products);

  const activeCategory = selectedCategory !== 'all' ? selectedCategory : null;
  const categoryLabel = activeCategory
    ? activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)
    : null;

  // Products after the category and search filters, before the sort filter.
  const filteredProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          (!activeCategory ||
            (product.category || '').trim() === activeCategory) &&
          product.title.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [products, activeCategory, searchQuery]
  );

  const visibleProducts = useMemo(
    () => sortProducts(filteredProducts, sort),
    [filteredProducts, sort]
  );

  // The canonical URL keeps ?category= because a category view is a genuine
  // landing page with its own heading and copy. Sort and search are dropped so
  // those variants are not treated as separate duplicate pages.
  const canonical = activeCategory
    ? `${SITE}/marketplace?category=${encodeURIComponent(activeCategory)}`
    : `${SITE}/marketplace`;

  // These mirror the wording produced server-side by api/render.js, so the
  // page title a crawler sees in the raw HTML matches the one React sets after
  // hydration.
  const totalForTitle = activeCategory
    ? products.filter(
        (product) => (product.category || '').trim() === activeCategory
      ).length
    : products.length;

  const seoTitle = categoryLabel
    ? `${categoryLabel} Digital Products | ${totalForTitle} Products | PokeShani`
    : `Digital Products Marketplace | ${products.length} Courses, Ebooks & Templates | PokeShani`;

  const seoDescription = categoryLabel
    ? `Browse ${totalForTitle} ${categoryLabel.toLowerCase()} digital products from independent creators on PokeShani. Instant download, secure PayPal checkout, zero platform fees.`
    : `Browse ${products.length} digital products from independent creators on PokeShani - online courses, ebooks, templates, graphics and more. Instant download, secure PayPal checkout, zero platform fees.`;

  const activeSortLabel =
    SORT_OPTIONS.find((option) => option.key === sort)?.label || 'Newest';

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h1 className="error-title">😕 {error}</h1>
        <p className="error-message">Please try again later</p>
      </div>
    );
  }

  return (
    <div className="marketplace-wrapper">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords="marketplace, digital products, courses, ebooks, templates, zero fees, instant payouts, free digital products"
        url={canonical}
        type="website"
      />
      <nav className="marketplace-nav">
        <div className="nav-left">
          <Link to="/" className="logo-link">
            <img src={logo} alt="PokeShani Logo" className="nav-logo" />
            <span className="nav-brand">PokeShani</span>
          </Link>
        </div>
        <div className="nav-center">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => updateParams({ q: e.target.value })}
              className="search-input"
              aria-label="Search products"
            />
          </div>
          <div className="category-dropdown">
            <select
              value={selectedCategory}
              onChange={(e) => updateParams({ category: e.target.value })}
              className="category-select"
              aria-label="Filter by category"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="category-dropdown">
            <select
              value={sort}
              onChange={(e) => updateParams({ sort: e.target.value })}
              className="category-select"
              aria-label="Sort products"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                  {option.key === 'free' && freeCount > 0 ? ` (${freeCount})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="nav-right">
          <Link to="/signup" className="nav-button signup-btn">Sign Up</Link>
        </div>
      </nav>

      <div className="marketplace-container">
        <div className="marketplace-content">
          <div className="marketplace-header">
            <h1 className="marketplace-title">
              {categoryLabel ? `${categoryLabel} Digital Products` : 'Digital Products Marketplace'}
            </h1>
          </div>

          <div className="products-section">
            <div className="products-row">
              {visibleProducts.slice(0, visibleCount).map((product) => (
                <div key={product.id} className="product-card">
                  <div className="product-media">
                    <img
                      src={product.thumbnail}
                      alt={product.title}
                      className="product-thumbnail"
                      loading="lazy"
                    />
                    {isFreeProduct(product) ? (
                      <span className="product-badge-free">Free</span>
                    ) : null}
                  </div>
                  <div className="product-info">
                    <h3 className="product-title">{product.title}</h3>
                    <div
                      className={`product-price${isFreeProduct(product) ? ' product-price-free' : ''}`}
                    >
                      {formatPrice(product)}
                    </div>
                    <div className="product-actions">
                      <a href={`/sales/${product.slug}`} className="buy-now-btn">
                        {isFreeProduct(product) ? 'Get for free' : 'Buy Now'}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* "Load more" instead of rendering the entire catalogue: each card
                downloads an image from Supabase Storage, which is the bandwidth
                the Free plan meters as Cached Egress. */}
            {visibleCount < visibleProducts.length ? (
              <div className="products-load-more">
                <button
                  type="button"
                  className="load-more-btn"
                  onClick={() => setVisibleCount((count) => count + UI_PAGE_SIZE)}
                >
                  Load {Math.min(UI_PAGE_SIZE, visibleProducts.length - visibleCount)} more
                </button>
                <p className="products-load-more-note">
                  Showing {Math.min(visibleCount, visibleProducts.length)} of{' '}
                  {visibleProducts.length} products
                </p>
              </div>
            ) : null}

            {visibleProducts.length === 0 ? (
              <div className="products-empty">
                <h3>No products match these filters</h3>
                <p>
                  {searchQuery
                    ? 'Try a different search term, or clear the filters to browse everything.'
                    : 'Try a different filter to see more products.'}
                </p>
                {sort !== 'new' || selectedCategory !== 'all' || searchQuery ? (
                  <button
                    type="button"
                    className="filter-pill"
                    onClick={() =>
                      updateParams({ sort: null, category: null, q: null })
                    }
                  >
                    Clear all filters
                  </button>
                ) : null}
                {sort === 'free' ? (
                  <p className="products-empty-hint">
                    There are no free products in this category right now. Try{' '}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => updateParams({ sort: 'price-asc', category: null })}
                    >
                      price: low to high
                    </button>{' '}
                    to see the cheapest paid options.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <footer className="marketplace-footer">
          <div className="footer-content">
            <div className="footer-bottom">
              <p>© 2025 PokeShani All rights reserved.</p>
              <div className="footer-links">
                <Link to="/privacy-policy" className="footer-link">
                  Privacy Policy
                </Link>
                <Link to="/terms-of-service" className="footer-link">
                  Terms of Service
                </Link>
                <Link to="/roadmap" className="footer-link">
                  Updates
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default MarketplacePage;
